import { effectiveGpuMb, formatOverridesFromSearch, gpuLabel, isAppleMobile as isAppleMobileDevice, probeAstcSupport } from "../walk/memory";
import { effectiveSearch } from "../walk/advanced";
import type { StorageUsage, StoredWalkSummary } from "../walk/storage";

export interface HomeScreenCallbacks {
  onFile(file: File): void;
  onOpenStored(id: string): void;
  onRenameStored(id: string, name: string): void;
  onDeleteStored(id: string): void;
  onClearAll(): void;
}

/** File-picker filter for platforms that honour file extensions. */
const WALK_ACCEPT = ".walk,.zip,application/zip,application/octet-stream";

/**
 * iOS/iPadOS turn `accept` entries into UTIs. `.walk` has no registered UTI
 * (it becomes a dynamic `dyn.*` type nothing matches), so the Files picker
 * greys .walk files out. There we show every file and let openWalk.ts reject
 * non-walks with a clear message. iPadOS 13+ reports itself as a Mac, so a
 * touch-capable "Mac" is treated as iPad.
 */
function isAppleMobile(): boolean {
  return isAppleMobileDevice(navigator.userAgent, navigator.maxTouchPoints);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString();
}

/** Home screen: open a new walk, or resume/manage ones already saved on this device. */
export class HomeScreen {
  readonly element: HTMLElement;
  private readonly status: HTMLParagraphElement;
  private readonly button: HTMLLabelElement;
  private readonly list: HTMLDivElement;
  private readonly empty: HTMLParagraphElement;
  private readonly usageEl: HTMLParagraphElement;

  constructor(private readonly callbacks: HomeScreenCallbacks) {
    this.element = document.createElement("main");
    this.element.id = "home";
    this.element.innerHTML = `
      <div class="home-card">
        <h1>Walk Player</h1>
        <p class="lead">Open a walk exported from Walk Bake.</p>
        <label class="btn primary open-btn">
          Open walk
          <input type="file" id="walk-input" hidden />
        </label>
        <p class="status" id="status" role="status"></p>
        <p class="hint">
          Opened walks are saved on this device so you can reopen them offline,
          even without picking the file again.
        </p>

        <div class="saved-walks">
          <div class="saved-header">
            <h2>Saved on this device</h2>
            <button class="link-btn" data-action="clear-all">Delete all</button>
          </div>
          <p class="empty-hint" id="empty-hint">Nothing saved yet.</p>
          <div id="walk-list"></div>
          <p class="usage" id="usage"></p>
        </div>
      </div>
    `;
    this.status = this.element.querySelector("#status") as HTMLParagraphElement;
    this.button = this.element.querySelector(".open-btn") as HTMLLabelElement;
    this.list = this.element.querySelector("#walk-list") as HTMLDivElement;
    this.empty = this.element.querySelector("#empty-hint") as HTMLParagraphElement;
    this.usageEl = this.element.querySelector("#usage") as HTMLParagraphElement;

    const input = this.element.querySelector("#walk-input") as HTMLInputElement;
    if (!isAppleMobile()) input.accept = WALK_ACCEPT;
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      input.value = "";
      if (file) callbacks.onFile(file);
    });

    this.element.querySelector('[data-action="clear-all"]')?.addEventListener("click", () => {
      if (confirm("Delete every walk saved on this device? This cannot be undone.")) {
        callbacks.onClearAll();
      }
    });

    // Desktop: drop a file anywhere on the page.
    this.element.addEventListener("dragover", (e) => {
      e.preventDefault();
      this.element.classList.add("dragover");
    });
    this.element.addEventListener("dragleave", () => this.element.classList.remove("dragover"));
    this.element.addEventListener("drop", (e) => {
      e.preventDefault();
      this.element.classList.remove("dragover");
      const file = e.dataTransfer?.files[0];
      if (file) callbacks.onFile(file);
    });
  }

  setBusy(busy: boolean, text = ""): void {
    this.button.classList.toggle("disabled", busy);
    this.status.classList.remove("error");
    this.status.textContent = text;
  }

  /** Shows an error line; `action` adds a button after it (e.g. Remove for an unreadable saved walk). */
  /** Modal question or notice. Resolves true for the confirm button, false for cancel. */
  private ask(message: string, confirmLabel: string, cancelLabel?: string): Promise<boolean> {
    return new Promise((resolve) => {
      const dialog = document.createElement("dialog");
      dialog.className = "memory-dialog";
      const text = document.createElement("p");
      text.textContent = message;
      const buttons = document.createElement("div");
      buttons.className = "dialog-actions";
      const confirm = document.createElement("button");
      confirm.className = "btn primary";
      confirm.textContent = confirmLabel;
      confirm.addEventListener("click", () => dialog.close("confirm"));
      if (cancelLabel) {
        const cancel = document.createElement("button");
        cancel.className = "btn";
        cancel.textContent = cancelLabel;
        cancel.addEventListener("click", () => dialog.close("cancel"));
        buttons.append(cancel);
      }
      buttons.append(confirm);
      dialog.append(text, buttons);
      dialog.addEventListener("close", () => {
        dialog.remove();
        resolve(dialog.returnValue === "confirm");
      });
      this.element.append(dialog);
      dialog.showModal();
    });
  }

  /** "Open anyway?" before a walk that is over the device's graphics-memory limit. */
  confirmOpen(message: string): Promise<boolean> {
    return this.ask(message, "Open", "Cancel");
  }

  /** One-off message, e.g. that the last walk closed while loading. */
  async notify(message: string): Promise<void> {
    await this.ask(message, "OK");
  }

  showError(message: string, action?: { label: string; run: () => void }): void {
    this.setBusy(false);
    this.status.classList.add("error");
    this.status.textContent = message;
    if (!action) return;
    const button = document.createElement("button");
    button.className = "link-btn danger";
    button.textContent = action.label;
    button.addEventListener("click", () => {
      button.disabled = true;
      action.run();
    });
    this.status.append(" ", button);
  }

  renderSavedWalks(walks: StoredWalkSummary[], usage: StorageUsage): void {
    this.empty.style.display = walks.length === 0 ? "block" : "none";
    this.list.innerHTML = "";
    for (const walk of walks) {
      this.list.appendChild(this.renderCard(walk));
    }
    const quota = usage.quotaBytes ? ` of ${formatBytes(usage.quotaBytes)}` : "";
    this.usageEl.textContent =
      walks.length > 0
        ? `${formatBytes(usage.usageBytes)}${quota} used on this device` +
          (usage.persisted ? "" : " · not protected from automatic cleanup")
        : "";
  }

  private renderCard(walk: StoredWalkSummary): HTMLElement {
    const effective = effectiveGpuMb(walk.gpuMb, walk.gpuMbUncompressed, probeAstcSupport(), formatOverridesFromSearch(effectiveSearch()));
    const card = document.createElement("div");
    card.className = "walk-card";
    card.innerHTML = `
      <div class="walk-card-main" data-action="open">
        <strong>${escapeHtml(walk.name)}</strong>
        <div class="meta">
          ${formatBytes(walk.fileSize)}${effective !== undefined ? ` · ${gpuLabel(effective)}` : ""} · saved ${formatDate(walk.importedAt)}
          ${walk.lastOpenedAt ? ` · last opened ${formatDate(walk.lastOpenedAt)}` : ""}
        </div>
      </div>
      <div class="walk-card-actions">
        <button class="link-btn" data-action="rename" aria-label="Rename">Rename</button>
        <button class="link-btn danger" data-action="delete" aria-label="Delete">Delete</button>
      </div>
    `;
    card.querySelector('[data-action="open"]')?.addEventListener("click", () => {
      this.callbacks.onOpenStored(walk.id);
    });
    card.querySelector('[data-action="rename"]')?.addEventListener("click", (e) => {
      e.stopPropagation();
      const name = prompt("Rename walk", walk.name);
      if (name && name.trim()) this.callbacks.onRenameStored(walk.id, name.trim());
    });
    card.querySelector('[data-action="delete"]')?.addEventListener("click", (e) => {
      e.stopPropagation();
      if (confirm(`Delete "${walk.name}" from this device?`)) this.callbacks.onDeleteStored(walk.id);
    });
    return card;
  }
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
